import { useState } from 'react';
import { useTodayEvent } from '../hooks/useEventData';
import { useAuth } from '../context/AuthContext';
import { useGroupData } from '../hooks/useGroupData';
import { formatDate, formatTime, getMapsUrl, formatPhone } from '../utils/helpers';
import { updateAttendance, claimDriving, releaseDriving } from '../api/client';
import EventBadge from '../components/EventBadge';
import LoadingSpinner from '../components/LoadingSpinner';
import type { AttendanceStatus } from '../types';

export default function TodayViewA() {
  const { user, kids: myKids } = useAuth();
  const { event, nextEvent, attendance, driving, loading, error, refresh } = useTodayEvent();
  const { members, kids: allKids } = useGroupData();
  const [actionLoading, setActionLoading] = useState<string | null>(null);

  const handleAttendance = async (kidId: string, current: AttendanceStatus) => {
    if (!event) return;
    const next: AttendanceStatus = current === 'going' ? 'not_going' : 'going';
    setActionLoading(`att-${kidId}`);
    try {
      await updateAttendance(event.id, kidId, next);
      await refresh();
    } finally {
      setActionLoading(null);
    }
  };

  const handleDrive = async (type: 'pickup' | 'return') => {
    if (!event) return;
    setActionLoading(`drive-${type}`);
    try {
      await claimDriving(event.id, type);
      await refresh();
    } finally {
      setActionLoading(null);
    }
  };

  const handleReleaseDrive = async (type: 'pickup' | 'return') => {
    if (!event) return;
    setActionLoading(`release-${type}`);
    try {
      await releaseDriving(event.id, type);
      await refresh();
    } finally {
      setActionLoading(null);
    }
  };

  const getDriverName = (driverId: string | null) => {
    if (!driverId) return null;
    return members.find(m => m.id === driverId)?.name || 'Unknown';
  };

  const getDriverPhone = (driverId: string | null) => {
    if (!driverId) return null;
    return members.find(m => m.id === driverId)?.phoneNumber || null;
  };

  const getKidAttendance = (kidId: string): AttendanceStatus => {
    return attendance.find(a => a.kidId === kidId)?.status || 'no_response';
  };

  if (loading) return <LoadingSpinner className="mt-20" />;
  if (error) return <div className="p-6 text-center text-cancelled">{error}</div>;

  // No event today
  if (!event) {
    return (
      <div className="px-5 pt-8 pb-6 max-w-lg mx-auto">
        <p className="text-sm font-medium tracking-wide uppercase text-gray-400 dark:text-gray-500">Today</p>
        <h1 className="text-2xl font-bold text-gray-900 dark:text-white mt-1">{formatDate(new Date().toISOString().split('T')[0])}</h1>

        <div className="mt-10 rounded-2xl bg-gray-50 dark:bg-gray-900 border border-gray-100 dark:border-gray-800 p-8 text-center">
          <div className="w-14 h-14 mx-auto rounded-full bg-gray-100 dark:bg-gray-800 flex items-center justify-center mb-4">
            <svg className="w-7 h-7 text-gray-400" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" d="M12 6v6h4.5m4.5 0a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          </div>
          <h2 className="text-lg font-semibold text-gray-700 dark:text-gray-300">No practice today</h2>
          <p className="text-sm text-gray-400 dark:text-gray-500 mt-1">Enjoy the rest day</p>
        </div>

        {nextEvent && (
          <div className="mt-6">
            <p className="text-xs font-medium tracking-wide uppercase text-gray-400 dark:text-gray-500 mb-3">Coming Up</p>
            <div className="rounded-2xl bg-white dark:bg-gray-900 border border-gray-100 dark:border-gray-800 p-5 shadow-sm">
              <div className="flex items-center gap-3">
                <EventBadge type={nextEvent.type} status={nextEvent.status} size="sm" />
                <span className="text-sm font-medium text-gray-600 dark:text-gray-300">{formatDate(nextEvent.date)}</span>
              </div>
              <p className="mt-2 text-sm text-gray-500 dark:text-gray-400">
                {formatTime(nextEvent.startTime)} - {formatTime(nextEvent.endTime)} &middot; {nextEvent.locationName}
              </p>
            </div>
          </div>
        )}
      </div>
    );
  }

  const pickupDriver = getDriverName(driving?.driverId || null);
  const pickupDriverPhone = getDriverPhone(driving?.driverId || null);
  const returnDriverId = driving?.returnDriverId || driving?.driverId || null;
  const returnDriver = getDriverName(returnDriverId);
  const returnDriverPhone = getDriverPhone(returnDriverId);
  const isMyPickup = driving?.driverId === user?.id;
  const isMyReturn = driving?.returnDriverId === user?.id;

  return (
    <div className="px-5 pt-8 pb-6 max-w-lg mx-auto">
      {/* Header */}
      <div className="flex items-start justify-between">
        <div>
          <p className="text-sm font-medium tracking-wide uppercase text-gray-400 dark:text-gray-500">Today</p>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white mt-1">{formatDate(event.date)}</h1>
        </div>
        <EventBadge type={event.type} status={event.status} size="lg" />
      </div>

      {event.status === 'cancelled' ? (
        <div className="mt-8 rounded-2xl bg-cancelled/5 dark:bg-cancelled/10 border border-cancelled/20 p-6 text-center">
          <p className="text-lg font-semibold text-cancelled">Event Cancelled</p>
          {event.notes && <p className="mt-2 text-sm text-gray-500 dark:text-gray-400">{event.notes}</p>}
        </div>
      ) : (
        <>
          {/* Hero Card — Time & Location */}
          <div className="mt-6 rounded-2xl bg-white dark:bg-gray-900 border border-gray-100 dark:border-gray-800 shadow-sm overflow-hidden">
            {event.title && (
              <div className="px-5 pt-5 pb-0">
                <h2 className="text-lg font-semibold text-gray-900 dark:text-white">{event.title}</h2>
              </div>
            )}
            <div className="px-5 py-5 space-y-4">
              {/* Time */}
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-gray-50 dark:bg-gray-800 flex items-center justify-center flex-shrink-0">
                  <svg className="w-5 h-5 text-gray-500 dark:text-gray-400" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M12 6v6h4.5m4.5 0a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
                <div>
                  <p className="text-base font-semibold text-gray-900 dark:text-white">
                    {formatTime(event.startTime)} - {formatTime(event.endTime)}
                  </p>
                  {event.pickupTime && (
                    <p className="text-sm text-gray-400 dark:text-gray-500">Pickup at {formatTime(event.pickupTime)}</p>
                  )}
                </div>
              </div>
              {/* Location */}
              <a
                href={getMapsUrl(event.locationName)}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-3 group"
              >
                <div className="w-10 h-10 rounded-xl bg-gray-50 dark:bg-gray-800 flex items-center justify-center flex-shrink-0 group-hover:bg-practice/10 dark:group-hover:bg-practice/20 transition-colors">
                  <svg className="w-5 h-5 text-gray-500 dark:text-gray-400 group-hover:text-practice transition-colors" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M15 10.5a3 3 0 11-6 0 3 3 0 016 0z" />
                    <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 10.5c0 7.142-7.5 11.25-7.5 11.25S4.5 17.642 4.5 10.5a7.5 7.5 0 1115 0z" />
                  </svg>
                </div>
                <div>
                  <p className="text-base font-medium text-gray-900 dark:text-white group-hover:text-practice dark:group-hover:text-practice-light transition-colors">
                    {event.locationName}
                  </p>
                  <p className="text-sm text-practice dark:text-practice-light">Get directions &rarr;</p>
                </div>
              </a>
              {/* Pickup Spot */}
              {driving?.pickupSpot && (
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-xl bg-gray-50 dark:bg-gray-800 flex items-center justify-center flex-shrink-0">
                    <svg className="w-5 h-5 text-gray-500 dark:text-gray-400" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M3 3v1.5M3 21v-6m0 0l2.77-.693a9 9 0 016.208.682l.108.054a9 9 0 006.086.71l3.114-.732a48.524 48.524 0 01-.005-10.499l-3.11.732a9 9 0 01-6.085-.711l-.108-.054a9 9 0 00-6.208-.682L3 4.5M3 15V4.5" />
                    </svg>
                  </div>
                  <div>
                    <p className="text-xs font-medium text-gray-400 dark:text-gray-500 uppercase tracking-wide">Pickup Spot</p>
                    <p className="text-sm font-medium text-gray-700 dark:text-gray-300">{driving.pickupSpot}</p>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Driver Card - TO */}
          <div className="mt-4 rounded-2xl bg-white dark:bg-gray-900 border border-gray-100 dark:border-gray-800 shadow-sm p-5">
            <p className="text-xs font-semibold tracking-wide uppercase text-gray-400 dark:text-gray-500 mb-3">Driver To Event</p>
            {pickupDriver ? (
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-green-50 dark:bg-green-900/30 flex items-center justify-center">
                    <svg className="w-5 h-5 text-green-600 dark:text-green-400" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M4.5 12.75l6 6 9-13.5" />
                    </svg>
                  </div>
                  <div>
                    <p className="font-semibold text-gray-900 dark:text-white">{pickupDriver}</p>
                    {pickupDriverPhone && <p className="text-sm text-gray-400">{formatPhone(pickupDriverPhone)}</p>}
                  </div>
                </div>
                {isMyPickup && (
                  <button
                    onClick={() => handleReleaseDrive('pickup')}
                    disabled={actionLoading === 'release-pickup'}
                    className="text-sm font-medium text-gray-400 hover:text-cancelled transition-colors min-h-tap flex items-center"
                  >
                    Release
                  </button>
                )}
              </div>
            ) : (
              <button
                onClick={() => handleDrive('pickup')}
                disabled={actionLoading === 'drive-pickup'}
                className="w-full py-3 rounded-xl bg-practice hover:bg-practice-dark text-white font-semibold transition-colors min-h-tap disabled:opacity-50"
              >
                {actionLoading === 'drive-pickup' ? 'Claiming...' : "I'll Drive"}
              </button>
            )}
          </div>

          {/* Driver Card - RETURN */}
          <div className="mt-3 rounded-2xl bg-white dark:bg-gray-900 border border-gray-100 dark:border-gray-800 shadow-sm p-5">
            <p className="text-xs font-semibold tracking-wide uppercase text-gray-400 dark:text-gray-500 mb-3">Return Driver</p>
            {driving?.returnDriverId ? (
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-green-50 dark:bg-green-900/30 flex items-center justify-center">
                    <svg className="w-5 h-5 text-green-600 dark:text-green-400" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M4.5 12.75l6 6 9-13.5" />
                    </svg>
                  </div>
                  <div>
                    <p className="font-semibold text-gray-900 dark:text-white">{returnDriver}</p>
                    {returnDriverPhone && <p className="text-sm text-gray-400">{formatPhone(returnDriverPhone)}</p>}
                  </div>
                </div>
                {isMyReturn && (
                  <button
                    onClick={() => handleReleaseDrive('return')}
                    disabled={actionLoading === 'release-return'}
                    className="text-sm font-medium text-gray-400 hover:text-cancelled transition-colors min-h-tap flex items-center"
                  >
                    Release
                  </button>
                )}
              </div>
            ) : pickupDriver ? (
              <div className="flex items-center justify-between">
                <p className="text-sm text-gray-400 dark:text-gray-500">
                  Defaults to {pickupDriver}
                </p>
                <button
                  onClick={() => handleDrive('return')}
                  disabled={actionLoading === 'drive-return'}
                  className="text-sm font-semibold text-practice hover:text-practice-dark dark:text-practice-light transition-colors min-h-tap flex items-center"
                >
                  {actionLoading === 'drive-return' ? 'Claiming...' : "I'll Drive Back"}
                </button>
              </div>
            ) : (
              <button
                onClick={() => handleDrive('return')}
                disabled={actionLoading === 'drive-return'}
                className="w-full py-3 rounded-xl border-2 border-dashed border-warning/40 hover:border-warning text-warning-dark dark:text-warning-light font-semibold transition-colors min-h-tap disabled:opacity-50"
              >
                {actionLoading === 'drive-return' ? 'Claiming...' : "I'll Drive Back"}
              </button>
            )}
          </div>

          {/* Attendance */}
          <div className="mt-6">
            <p className="text-xs font-semibold tracking-wide uppercase text-gray-400 dark:text-gray-500 mb-3 px-1">Attendance</p>
            <div className="space-y-2">
              {allKids.map(kid => {
                const status = getKidAttendance(kid.id);
                const isMine = myKids.some(k => k.id === kid.id) || user?.role === 'admin';
                const parentName = members.find(m => m.id === kid.parentId)?.name;
                return (
                  <button
                    key={kid.id}
                    onClick={() => isMine && handleAttendance(kid.id, status)}
                    disabled={!isMine || actionLoading === `att-${kid.id}`}
                    className={`w-full flex items-center justify-between px-5 py-3.5 rounded-xl transition-all min-h-tap ${
                      status === 'going'
                        ? 'bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800'
                        : status === 'not_going'
                        ? 'bg-gray-50 dark:bg-gray-900 border border-gray-100 dark:border-gray-800 opacity-60'
                        : 'bg-white dark:bg-gray-900 border border-gray-100 dark:border-gray-800'
                    } ${isMine ? 'active:scale-[0.98]' : 'cursor-default'}`}
                  >
                    <div className="text-left">
                      <p className={`font-medium ${status === 'not_going' ? 'text-gray-400 dark:text-gray-500 line-through' : 'text-gray-900 dark:text-white'}`}>
                        {kid.name}
                      </p>
                      {parentName && <p className="text-xs text-gray-400 dark:text-gray-500">{parentName}</p>}
                    </div>
                    <div className="flex items-center gap-2">
                      {status === 'going' && (
                        <span className="text-xs font-semibold text-green-600 dark:text-green-400 bg-green-100 dark:bg-green-900/40 px-2 py-0.5 rounded-full">Going</span>
                      )}
                      {status === 'not_going' && (
                        <span className="text-xs font-semibold text-gray-400 bg-gray-100 dark:bg-gray-800 px-2 py-0.5 rounded-full">Not going</span>
                      )}
                      {status === 'no_response' && (
                        <span className="text-xs font-semibold text-warning bg-warning/10 px-2 py-0.5 rounded-full">No response</span>
                      )}
                    </div>
                  </button>
                );
              })}
            </div>
          </div>

          {/* Notes */}
          {event.notes && (
            <div className="mt-6 rounded-2xl bg-amber-50/60 dark:bg-amber-900/10 border border-amber-100 dark:border-amber-900/30 p-5">
              <p className="text-xs font-semibold tracking-wide uppercase text-amber-600/70 dark:text-amber-400/70 mb-1">Notes</p>
              <p className="text-sm text-amber-900 dark:text-amber-200">{event.notes}</p>
            </div>
          )}
        </>
      )}
    </div>
  );
}
